import { ReactElement, useEffect } from 'react'
import { gql, useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'
import { isEmpty } from 'lodash'
import styled from 'styled-components'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import FormLayout from 'features/App/components/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { FEATURES, PERMISSIONS } from 'features/Roles/components/Permissions/constants'
import NewAddonService from 'features/Ancillaries/components/NewAddonService'
import { mapAddonServiceFormToQuery, mapAddonServiceQueryToForm } from 'resolvers/addonServices'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { getDifference } from 'utils/object'
import { IAddonServiceForm } from 'features/Ancillaries/types'

const FormLayoutContent = styled(FormLayout.Content)`
  margin-bottom: 200px;
`

const GET_ADDON_SERVICE = gql`
  query GetAddonService($addonServiceId: ID!) {
    serviceFeeAddonRule(id: $addonServiceId) {
      id
      company {
        id
        name
      }
      companyLine {
        id
        name
      }
      salesChannelsV2 {
        id
        name
        country {
          id
          currency {
            id
            code
          }
        }
      }
      confirmationModalities
      segments {
        id
        origin {
          id
          name
          code
        }
        destination {
          id
          name
          code
        }
      }
      ticketFees {
        quantity
        fees {
          amount
          currencyCode
        }
      }
    }
  }
`

const CHANGE_ADDON_SERVICE = gql`
  mutation ChangeAddonService($addonService: ChangeServiceFeeAddonRuleInput!) {
    changeServiceFeeAddonRule(input: $addonService)
  }
`

const messages = defineMessages({
  helmet: {
    id: 'addonService.edit.helmet',
    defaultMessage: 'Edit Addon Service | Nexbus Admin'
  }
})

const EditAddonServiceView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const navigate = useNavigate()
  const { addonServiceId } = useParams()
  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_ADDON_SERVICE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      addonServiceId
    }
  })
  const [changeAddonService, { loading: mutationLoading, error: mutationError }] = useMutation(CHANGE_ADDON_SERVICE, {
    onCompleted: (): TSFixMe => navigate('/ancillaries/services')
  })

  let oldAddonService: IAddonServiceForm
  let content: ReactElement

  const handleSubmit = (addonService: IAddonServiceForm): void => {
    const oldMappedAddonService = mapAddonServiceFormToQuery(oldAddonService)
    const newMappedAddonService = mapAddonServiceFormToQuery(addonService)
    const payload = getDifference(newMappedAddonService, oldMappedAddonService)

    if (!isEmpty(payload)) {
      changeAddonService({
        variables: {
          addonService: {
            ...payload,
            id: addonServiceId,
            companyID: newMappedAddonService.companyID
          }
        }
      })
    } else {
      navigate('/ancillaries/services')
    }
  }

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
    refetch()
  }, [])

  if (!canWrite(FEATURES.ANCILLARIES, login.accessToken.policies)) {
    return <ForbiddenView feature={PERMISSIONS.COMPANY_INSURANCE_SETTINGS_READ} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="addonService.edit.error.description"
              defaultMessage="There was an error while loading your addon service"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetch()
              }}
              kind="primary"
            >
              <FormattedMessage id="addonService.edit.error.action" defaultMessage="Reload your addon service" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    oldAddonService = mapAddonServiceQueryToForm(queryData && queryData.serviceFeeAddonRule)

    content = (
      <NewAddonService
        addonService={oldAddonService}
        onSubmit={handleSubmit}
        error={mutationError}
        loading={mutationLoading}
        isEdit
      />
    )
  }

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="addonService.edit.title" defaultMessage="Edit addon service" />
        </Heading.H3>
      </FormLayout.Header>

      <FormLayoutContent>
        <Card.Container>{content}</Card.Container>
      </FormLayoutContent>
    </div>
  )
}

export default EditAddonServiceView
